import React, { useState, useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet-polylinedecorator";
import "./Cluster.css";
import CMarker from "../../../../Content/images/gmaps-marker.svg";
import styles from "./FmMap.module.css";

const POLYLINE_COLORS = {
  DEFAULT: "#007AFF", // Blue
  HOVER: "#3399FF", // Light Blue
  MANGO: "#FFC300", // Mango
  SELECTED: "#28A745", // Vibrant Green
  RED: "#E34234", // Vibrant Green
};

const Map = ({ network1 = [] }) => {
  const [selectedLane, setSelectedLane] = useState(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const canvasRenderer = L.canvas();
  const polylineDecorators = useRef([]);

  useEffect(() => {
    if (!mapInstance.current) {
      mapInstance.current = L.map(mapRef.current).setView(
        [20.5937, 78.9629], // Initial center position (India's lat, lon)
        4.45 // Initial zoom level
      );

      L.tileLayer(
        // wikimedia - blocked for production use
        // "https://maps.wikimedia.org/osm-intl/{z}/{x}/{y}{r}.png?lang=en",
        "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
        {}
      ).addTo(mapInstance.current);
    }
  }, []);

  const removePolylineDecorator = () => {
    polylineDecorators.current.forEach((decorator) => {
      mapInstance.current.removeLayer(decorator);
    });

    // clear all decorators
    polylineDecorators.current = [];
  };

  const handlePolylineHover = (clickablePolyline, visiblePolyline) => {
    clickablePolyline.on("mouseover", function () {
      visiblePolyline.setStyle({
        weight: 4,
        opacity: 1,
      });
    });

    clickablePolyline.on("mouseout", function () {
      visiblePolyline.setStyle({
        weight: 0.5,
        opacity: 0.3,
        color: POLYLINE_COLORS.DEFAULT,
      });
    });
  };

  const handlePolylineClick = (network, clickablePolyline) => {
    const origin = [
      network.origin?.location?.lat,
      network.origin?.location?.lon,
    ];
    const destination = [
      network.destination?.location?.lat,
      network.destination?.location?.lon,
    ];

    const newSelectedLane = {
      origin: network.origin,
      destination: network.destination,
      truck_type: network.truck_type,
      polyline: clickablePolyline,
    };

    // update selected lane with clicked polyline
    setSelectedLane((prev) => {
      if (prev && prev.polyline !== newSelectedLane.polyline) {
        // reset previous polyline styles
        prev.polyline.setStyle({
          weight: 12,
          opacity: 0,
          color: POLYLINE_COLORS.DEFAULT,
        });
        removePolylineDecorator();
      }

      // new lane's polyline style
      clickablePolyline.setStyle({
        weight: 3,
        opacity: 0.6,
        color: POLYLINE_COLORS.RED,
      });

      // arrow for the selected lane
      const polylineDecorator = L.polylineDecorator(clickablePolyline, {
        patterns: [
          {
            endOffset: "0%",
            repeat: 30,
            symbol: L.Symbol.arrowHead({
              pixelSize: 6,
              polygon: false,
              pathOptions: {
                stroke: true,
                color: POLYLINE_COLORS.MANGO,
                weight: 1,
                opacity: 0.8,
              },
            }),
          },
        ],
      }).addTo(mapInstance.current);
      polylineDecorators.current.push(polylineDecorator);

      // midpoint of origin and destination
      const midPointLat = (origin[0] + destination[0]) / 2;
      const midPointLon = (origin[1] + destination[1]) / 2;

      // distance between origin and destination
      const distance = mapInstance.current.distance(origin, destination);

      // zoom level based on distance
      let zoomLevel;
      if (distance > 500000) {
        zoomLevel = 5; // For large distances, zoom out
      } else if (distance > 100000) {
        zoomLevel = 6; // Medium distances, moderate zoom
      } else {
        zoomLevel = 8; // For small distances, zoom in
      }

      // readjust zoom level for within valid range - (3, 10)
      zoomLevel = Math.min(Math.max(zoomLevel, 3), 10);

      mapInstance.current.setView([midPointLat, midPointLon], zoomLevel, {
        animate: true,
        duration: 1,
      });

      return newSelectedLane;
    });

    // markers for origin and destination
    L.marker(origin, {
      icon: L.icon({
        iconUrl: CMarker,
        iconSize: [30, 30],
      }),
    }).addTo(mapInstance.current);

    L.marker(destination, {
      icon: L.icon({
        iconUrl: CMarker,
        iconSize: [30, 30],
      }),
    }).addTo(mapInstance.current);
  };

  const panToLocation = (lat, lon) => {
    mapInstance.current.panTo([lat, lon], {
      animate: true,
      duration: 1,
    });
  };

  useEffect(() => {
    if (mapInstance.current) {
      mapInstance.current.eachLayer((layer) => {
        if (layer instanceof L.Polyline) {
          mapInstance.current.removeLayer(layer);
        }
        if (layer instanceof L.Marker) {
          mapInstance.current.removeLayer(layer);
        }
      });

      network1.forEach((network) => {
        if (
          network.origin?.location?.lat &&
          network.origin?.location?.lon &&
          network.destination?.location?.lat &&
          network.destination?.location?.lon
        ) {
          const origin = [
            network.origin?.location?.lat,
            network.origin?.location?.lon,
          ];
          const destination = [
            network.destination?.location?.lat,
            network.destination?.location?.lon,
          ];

          // Invisible wider polyline for better clicking
          const clickablePolyline = L.polyline([origin, destination], {
            renderer: canvasRenderer,
            color: POLYLINE_COLORS.DEFAULT,
            weight: 12, // wider
            opacity: 0, // invisible
            interactive: true, // clickable
          }).addTo(mapInstance.current);

          // visible polyline
          const visiblePolyline = L.polyline([origin, destination], {
            renderer: canvasRenderer,
            color: POLYLINE_COLORS.DEFAULT,
            weight: 0.5,
            opacity: 0.3,
            interactive: false, // non clickable
          }).addTo(mapInstance.current);

          //  arrow to clicked lane's polyline
          L.polylineDecorator(visiblePolyline, {
            patterns: [
              {
                endOffset: "0%",
                repeat: 40,
                symbol: L.Symbol.arrowHead({
                  pixelSize: 6,
                  polygon: false,
                  pathOptions: {
                    stroke: true,
                    color: POLYLINE_COLORS.HOVER,
                    weight: 2,
                    opacity: 0.2,
                  },
                }),
              },
            ],
          }).addTo(mapInstance.current);

          handlePolylineHover(clickablePolyline, visiblePolyline);

          // on click of event of invisible polyline
          clickablePolyline.on("click", () => {
            handlePolylineClick(network, clickablePolyline);
          });
        }
      });
    }
  }, [network1]);

  return (
    <div className={`position-relative px-3 pt-3 ${styles.containerStyle}`}>
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      <div
        className={`info-window position-absolute p-3 px-4 border rounded ${styles.infoWindowStyles}`}
      >
        {selectedLane ? (
          <div>
            <div>
              <div className="d-flex justify-content-between pb-2">
                <strong>Origin:</strong>
                <button
                  className="btn btn-sm btn-link ml-2 p-0"
                  onClick={() =>
                    panToLocation(
                      selectedLane.origin?.location?.lat,
                      selectedLane.origin?.location?.lon
                    )
                  }
                >
                  Pan to Origin
                </button>
              </div>
              <div className="text-wrap">
                {selectedLane.origin?.location?.label}
              </div>
              <div className="text-muted" style={{ fontSize: "11px" }}>
                {selectedLane.origin?.addressLine1}
              </div>
            </div>
            <hr />
            <div>
              <div className="d-flex justify-content-between pb-2">
                <strong>Destination:</strong>
                <button
                  className="btn btn-sm btn-link ml-2 p-0"
                  onClick={() =>
                    panToLocation(
                      selectedLane.destination?.location?.lat,
                      selectedLane.destination?.location?.lon
                    )
                  }
                >
                  Pan to Destination
                </button>
              </div>
              <div className="text-wrap">
                {selectedLane.destination?.location?.label}
              </div>
              <div className="text-muted" style={{ fontSize: "11px" }}>
                {selectedLane.destination?.addressLine1}
              </div>
            </div>
            <hr />
            <div>
              <strong>Truck:</strong>
              <div className="text-wrap">{selectedLane?.truck_type?.value}</div>
            </div>
          </div>
        ) : (
          <div>Click on any lane to view details.</div>
        )}
      </div>
    </div>
  );
};

export default Map;
